'use client';

import { type AriaAttributes, type AriaRole } from 'react';
import { useIsClient } from 'usehooks-ts';

import { cn } from '@/utils/ui';

interface IconType extends React.SVGProps<SVGSVGElement> {
  id: string;
  alt?: string;
  decorative?: boolean;
  size?: number;
}

interface IconAccessibilityProps extends Pick<AriaAttributes, 'aria-hidden' | 'aria-label'> {
  role?: AriaRole;
  tabIndex?: number;
}

export function Icon(props: IconType): React.ReactNode {
  const { alt, decorative = false, color, id, size, onClick, className = '', ...rest } = props;

  const isLocalDevelopment = process.env.NODE_ENV === 'development';
  const isClient = useIsClient();

  const sizeClassName = size ? `size-[${size}px]` : '';

  let sessionCacheHash = '';

  if (typeof window !== 'undefined') {
    sessionCacheHash = window.sessionStorage.getItem('cacheHash') ?? '';
  }

  const cacheBustHash = isLocalDevelopment && sessionCacheHash && isClient ? `?v=${sessionCacheHash}` : '';

  const getAccessibilityProps = (): IconAccessibilityProps => {
    if (decorative) {
      return { 'aria-hidden': 'true' };
    }

    if (onClick) {
      return {
        role: 'button',
        'aria-label': alt || `${id} button`,
        tabIndex: 0,
      };
    }

    if (alt) {
      return {
        'aria-label': alt,
      };
    }

    // Fallback for unlabeled meaningful icons
    return {
      'aria-label': id.replace(/[-_]/g, ' '),
    };
  };

  return (
    <svg
      onClick={onClick}
      className={cn(sizeClassName, className)}
      color={color}
      data-testid={`${id}-icon`}
      {...getAccessibilityProps()}
      {...rest}
    >
      <use href={`/symbols.svg${cacheBustHash}#${id}`} />
    </svg>
  );
}
